/**
 * useAutoGrowTextarea — grow a <textarea> to fit its content, capped at a max.
 *
 * The composer starts at a single line and expands line-by-line as the writer
 * types (or pastes) longer prompts, then stops at `maxHeightPx` and scrolls
 * internally. There is no autosize library in the repo — this is the small,
 * scrollHeight-based primitive that the four other raw textareas can adopt
 * later.
 *
 * # DECISION: keyed on `value` via useLayoutEffect. Every path that mutates the
 * text — typing, clearing on submit, `prefillInput`, `setReplyTarget` — flows
 * through the controlled `value`, so keying the resize on it covers all of them
 * for free (including collapsing back to one line when the field is cleared).
 * useLayoutEffect corrects the height before paint so the field never flashes
 * at the wrong size.
 */

import { useCallback, useLayoutEffect, useRef } from "react";

export function useAutoGrowTextarea(value: string, maxHeightPx: number) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // Save the scroll offset across the measure. Setting height to "auto" makes
    // the field briefly tall enough for all its content, so nothing overflows
    // and the browser clamps scrollTop to 0; restoring the capped height leaves
    // it there. Past the cap that snapped the visible window back to the TOP of
    // the text on every keystroke, putting the line being typed — and the caret
    // — below the fold, which reads as the text having vanished. The saved
    // value is already caret-correct: the browser scrolls to the caret on the
    // input event, before this layout effect runs.
    const scrollTop = el.scrollTop;
    // Reset to "auto" first so scrollHeight reflects the *content* height and
    // the field can shrink as well as grow (without the reset, scrollHeight
    // never drops below the current fixed height).
    el.style.height = "auto";
    const next = Math.min(el.scrollHeight, maxHeightPx);
    el.style.height = `${next}px`;
    // Only show the scrollbar once we've hit the cap — below it the field is
    // exactly content-height, so a scrollbar would be dead chrome.
    el.style.overflowY = el.scrollHeight > maxHeightPx ? "auto" : "hidden";
    // Restore last, after the final height is in place. The browser clamps an
    // over-large value to the new scroll range, so a grow shrinks it correctly.
    el.scrollTop = scrollTop;
  }, [maxHeightPx]);

  // `value` is the reactive trigger — resize() measures the DOM, not `value`,
  // but the field must be re-measured on every text change. resize is stable
  // across value changes (depends only on maxHeightPx), so it alone would
  // never re-fire.
  // biome-ignore lint/correctness/useExhaustiveDependencies: value is an intentional re-measure trigger, not read inside the effect
  useLayoutEffect(() => {
    resize();
  }, [value, resize]);

  return { ref, resize };
}

export default useAutoGrowTextarea;
